import React, { useState } from 'react';
import { useConfirm } from '../../context/ConfirmContext';
import { decideDuplicate, errorMessage, recheckSimilarity } from '../../api/catalogImportApi';
import UpdateExistingModal from './UpdateExistingModal';
import AddVariantModal from './AddVariantModal';
import ComparisonNotebook from './ComparisonNotebook';
import { btnGhost, btnPrimary, btnSmall, pill } from './styles';

const inr = (v) => (v === null || v === undefined ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);

const VERDICT = {
  SAME: { text: 'Same product', style: pill('#FEE2E2', '#B91C1C') },
  VARIANT: { text: 'Variant', style: pill('#FEF3C7', '#92400E') },
  DIFFERENT: { text: 'Different', style: pill('#F3F4F6', '#4B5563') },
};

const SOURCE = { RULE_NAME: 'name rule', RULE_PHOTO: 'photo match', AI: 'AI', NAME_MATCH: 'name match' };

/** Mirrors the backend: a human decides unless the AI is confident it's a different product. */
const needsDecision = (c) => !c.dismissed && (!c.verdict || c.verdict !== 'DIFFERENT' || c.confidence === 'LOW');

function Thumb({ url, alt }) {
  return url
    ? <img src={url} alt={alt} style={{ width: '100%', height: 100, objectFit: 'contain', display: 'block', background: '#fff' }} />
    : <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#9CA3AF' }}>No image</div>;
}

/**
 * "Similar item detected": the draft next to look-alike products already in the catalogue, each
 * with the AI's verdict and reason. The reviewer resolves it one of three ways — keep the existing
 * product, update the existing product from the catalog, or create a new one.
 */
export default function DuplicatePanel({ item, campaign, onItemChanged, onError }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [updating, setUpdating] = useState(null); // productId being updated
  const [addingOption, setAddingOption] = useState(null); // productId getting a new option
  const [comparing, setComparing] = useState(null); // index into the notebook's look-alikes
  const candidates = item.duplicateCandidates || [];
  const open = candidates.filter(needsDecision);
  const settled = candidates.filter((c) => !needsDecision(c));
  const notebookOrder = [...open, ...settled]; // ones needing a decision first

  const run = async (fn, fallback) => {
    setBusy(true);
    try {
      onItemChanged((await fn()).data);
    } catch (err) {
      onError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const recheck = async () => {
    setChecking(true);
    try {
      onItemChanged((await recheckSimilarity(item.id)).data);
    } catch (err) {
      onError(errorMessage(err, 'The similarity check failed — try again.'));
    } finally {
      setChecking(false);
    }
  };

  const keepExisting = async (c) => {
    const ok = await confirm({
      title: `Use existing product #${c.productId}?`,
      message: `"${item.name || 'This draft'}" will be linked to "${c.name}" — no new product is created.`
        + (campaign ? ` The existing product is added to the campaign "${campaign.name}".` : ''),
      confirmLabel: campaign ? 'Keep existing & add to campaign' : 'Keep existing',
    });
    if (ok) run(() => decideDuplicate(item.id, { decision: 'KEEP_EXISTING', productId: c.productId }), 'Could not link the product.');
  };

  const notSame = () => run(() => decideDuplicate(item.id, { decision: 'DIFFERENT' }), 'Could not save the decision.');

  const stale = item.similarityStatus === 'STALE';
  const recheckButton = (
    <button style={{ ...btnSmall }} disabled={checking || busy} onClick={recheck}>
      {checking ? 'Checking…' : stale ? 'Re-check similar items' : 'Check for similar items'}
    </button>
  );

  // Nothing similar, or everything already settled.
  if (open.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12, color: '#6B7280', margin: '0 0 14px' }}>
        {stale ? (
          <span style={{ color: '#92400E' }}>Name, brand or category changed since the similarity check.</span>
        ) : candidates.length === 0 ? (
          item.similarityStatus === 'CHECKED' ? <span>✓ AI checked — nothing similar in the catalogue.</span> : null
        ) : (
          <details>
            <summary style={{ cursor: 'pointer' }}>
              ✓ {candidates.some((c) => c.dismissed) ? 'Marked as a different product from' : 'AI checked'} {candidates.length} similar item{candidates.length > 1 ? 's' : ''} — not the same
            </summary>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {candidates.map((c) => (
                <li key={c.productId}>
                  <a href={`/product/${c.productId}`} target="_blank" rel="noreferrer">#{c.productId} {c.name}</a>
                  {c.reason && <> — {c.reason}</>}
                </li>
              ))}
            </ul>
          </details>
        )}
        {(stale || item.similarityStatus === 'UNCHECKED') && recheckButton}
      </div>
    );
  }

  const main = item.images?.[0];
  return (
    <section aria-label="Similar item detected" style={{ border: '1.5px solid #C4B5FD', background: '#F5F3FF', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <strong style={{ fontSize: 14, color: '#4C1D95' }}>Similar item detected</strong>
          <div style={{ fontSize: 12, color: '#5B21B6' }}>
            {item.similarityStatus === 'CHECKED' ? (item.duplicateCandidates || []).some((c) => c.source === 'AI') ? 'Checked by name rules, photo matching and — where those couldn’t tell — AI. You decide.' : 'Checked by name rules and photo matching. You decide.'
              : stale ? 'Details changed since the AI check — re-check for up-to-date verdicts.'
              : 'Matched by name only — not AI-checked yet.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {item.similarityStatus !== 'CHECKED' && recheckButton}
          <button style={btnPrimary} disabled={busy} onClick={() => setComparing(0)}>Compare side by side</button>
          <button style={btnGhost} disabled={busy} onClick={notSame}>Not the same — create new</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <figure style={{ margin: 0, width: 170, padding: 8, background: '#fff', border: '1.5px dashed #C4B5FD', borderRadius: 8, boxSizing: 'border-box' }}>
          <Thumb url={main?.url} alt="This draft" />
          <figcaption style={{ fontSize: 12, marginTop: 6 }}>
            <strong>This draft</strong>
            <div style={{ color: '#374151' }}>{item.name || '—'}</div>
            <div style={{ color: '#6B7280' }}>{item.brandName || '—'} · {inr(item.mrp)}</div>
          </figcaption>
        </figure>

        {open.map((c) => {
          const v = c.verdict ? VERDICT[c.verdict] : null;
          return (
            <figure key={c.productId} style={{ margin: 0, width: 220, padding: 8, background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 8, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <Thumb url={c.imageUrl} alt={c.name} />
              <figcaption style={{ fontSize: 12, marginTop: 6, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                  {v ? <span style={v.style}>{v.text}</span> : <span style={pill('#EDE9FE', '#5B21B6')}>Name match</span>}
                  {c.source && SOURCE[c.source] && c.verdict && <span style={pill('#F9FAFB', '#6B7280')} title="How this verdict was reached">{SOURCE[c.source]}</span>}
                  {c.source === 'AI' && c.confidence && <span style={pill('#F9FAFB', '#6B7280')}>{c.confidence.toLowerCase()} confidence</span>}
                </div>
                <a href={`/product/${c.productId}`} target="_blank" rel="noreferrer" style={{ fontWeight: 700, color: '#111827' }}>
                  #{c.productId} {c.name} ↗
                </a>
                <div style={{ color: '#6B7280', margin: '2px 0 4px' }}>
                  {c.brand || '—'} · {inr(c.mrp)}
                  {c.approvalStatus && c.approvalStatus !== 'APPROVED' && <> · {c.approvalStatus.toLowerCase()}</>}
                </div>
                {c.reason && <div style={{ color: '#4B5563', fontStyle: 'italic', marginBottom: 8 }}>“{c.reason}”</div>}
                <button style={{ ...btnSmall, marginBottom: 6 }} disabled={busy} onClick={() => setComparing(notebookOrder.indexOf(c))}>
                  Compare side by side
                </button>
                <div style={{ display: 'flex', flexDirection: c.verdict === 'VARIANT' ? 'column-reverse' : 'column', gap: 6, marginTop: 'auto' }}>
                  {/* A VARIANT verdict puts "add as option" first. */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button style={{ ...btnPrimary, padding: '6px 10px', fontSize: 12 }} disabled={busy} onClick={() => keepExisting(c)}>
                      Keep existing{campaign ? ' & add' : ''}
                    </button>
                    <button style={{ ...btnSmall }} disabled={busy} onClick={() => setUpdating(c.productId)}>
                      Update existing from catalog…
                    </button>
                  </div>
                  <button style={c.verdict === 'VARIANT' ? { ...btnPrimary, padding: '6px 10px', fontSize: 12 } : { ...btnSmall }}
                          disabled={busy} onClick={() => setAddingOption(c.productId)}>
                    Add as an option of this product…
                  </button>
                </div>
              </figcaption>
            </figure>
          );
        })}
      </div>

      {settled.length > 0 && (
        <details style={{ marginTop: 10, fontSize: 12, color: '#6B7280' }}>
          <summary style={{ cursor: 'pointer' }}>Also compared: {settled.length} item{settled.length > 1 ? 's' : ''} judged different</summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {settled.map((c) => (
              <li key={c.productId}>
                <a href={`/product/${c.productId}`} target="_blank" rel="noreferrer">#{c.productId} {c.name}</a>
                {c.reason && <> — {c.reason}</>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {comparing !== null && (
        <ComparisonNotebook
          item={item}
          candidates={notebookOrder}
          startIndex={comparing}
          campaign={campaign}
          busy={busy}
          onClose={() => setComparing(null)}
          // Close the notebook first so confirmations and the update/option dialogs aren't hidden behind it.
          onKeepExisting={(c) => { setComparing(null); keepExisting(c); }}
          onUpdateExisting={(id) => { setComparing(null); setUpdating(id); }}
          onAddOption={(id) => { setComparing(null); setAddingOption(id); }}
          onNotSame={() => { setComparing(null); notSame(); }}
        />
      )}
      {addingOption && (
        <AddVariantModal
          item={item}
          productId={addingOption}
          campaign={campaign}
          onClose={() => setAddingOption(null)}
          onDone={(updated) => { setAddingOption(null); onItemChanged(updated); }}
        />
      )}
      {updating && (
        <UpdateExistingModal
          item={item}
          productId={updating}
          campaign={campaign}
          onClose={() => setUpdating(null)}
          onDone={(updated) => { setUpdating(null); onItemChanged(updated); }}
        />
      )}
    </section>
  );
}
